<template>
  <nav v-if="currentUser" class="navigation">
    <div class="menu">
      <ul id="left-menu">
        <NavDropdown
          v-for="menu in menus"
          :key="menu.label"
          :label="menu.label"
          :items="menu.items"
          :current-open="currentOpenMenuId"
          @i-am-open="handleOpenItem($event)"
        />
      </ul>

      <ul id="right-menu">
        <li class="lang">
          <a @click="toggleLang($i18n.locale)">
            <font-awesome-icon icon="globe" />
            {{ lang }}
          </a>
        </li>
        <li class="notification">
          <router-link to="/notifications/listing">
            <font-awesome-icon icon="bell" :class="{ unread: unreadNotificationCount }" />
            <div v-if="unreadNotificationCount">
              {{ unreadNotificationCount }}
            </div>
          </router-link>
        </li>
      </ul>
      <ul class="small-screen-menu-icon">
        <li>
          <a @click="toggleSmallScreenMenu()">
            <font-awesome-icon icon="bars" />
          </a>
        </li>
      </ul>
    </div>
    <div
      class="small-screen-menu"
      :class="{ 'menu-is-up': isSmallScreenMenuVisible }"
      @click="toggleSmallScreenMenu()"
    >
      <ul>
        <NavDropdown
          v-for="menu in menus"
          :key="menu.label"
          :label="menu.label"
          :items="menu.items"
          @show-dropdown="fadeSearchBar()"
        />
      </ul>
    </div>
    <div class="quick-search">
      <tol-type-ahead
        v-if="isTesEmployee"
        :search-params="{
          url: `/search/global-quick-search`,
          searchKeys: ['searchParam']
        }"
        :icon-config="{ label: 'search' }"
        :show-spinner="showSpinner"
        @search-in-progress="spinnerData"
      >
        <template v-slot="{ value }">
          <router-link :to="`/${value.URLPart}/${value.referenceNumber}`">
            <font-awesome-icon v-if="value.icon" :icon="value.icon" />
            {{ value.label }} {{ `(${value.name})` }}
          </router-link>
        </template>
      </tol-type-ahead>
    </div>
  </nav>
</template>

<script>
import NavDropdown from '@/app/components/NavDropdown';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import { AppActions } from '@/app/app.keys';
import {
  RecentItemsActions,
  RecentItemsGetters
} from '@/app/features/recent-items/recent-items.keys';

import { NotificationsListingGetters } from '@/app/features/notifications/features/notifications-listing/notifications-listing.keys';
import { userUtils } from '@/mixins/user-utils';

export default {
  name: 'Navigation',
  components: {
    NavDropdown,
    TolTypeAhead
  },
  mixins: [userUtils],
  data() {
    return {
      devMode: process.env.NODE_ENV === 'development',
      isSmallScreenMenuVisible: false,
      showSpinner: false,
      currentOpenMenuId: null
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    unreadNotificationCount() {
      return this.$store.getters[NotificationsListingGetters.TotalUnreadNotifications];
    },
    menus() {
      const checkPermissions = item =>
        item.permission === null ||
        (item.permission in this.currentUser.permissions &&
          'VIEW' in this.currentUser.permissions[item.permission]);

      const menus = [
        {
          label: this.$t('app.navigation.menu'),
          items: [
            {
              label: this.$t('app.navigation.home'),
              permission: null,
              route: '/',
              icon: 'home'
            },
            {
              label: this.$t('app.navigation.changes'),
              permission: 'CHANGES',
              route: '/changes',
              icon: 'pencil-alt'
            },
            {
              label: this.$t('app.navigation.placementActivity'),
              permission: 'PLACEMENT_ACTIVITY',
              route: '/placement-activity',
              icon: 'file-alt'
            },
            {
              label: this.$t('app.navigation.reports'),
              permission: 'REPORT',
              route: '/reports',
              icon: 'file-alt'
            },
            { divider: true },
            {
              label: this.$t('app.navigation.admin'),
              permission: 'ADMIN',
              route: '/admin',
              icon: 'robot'
            }
          ].filter(checkPermissions)
        },
        {
          label: this.$t('app.navigation.records'),
          items: [
            {
              label: this.$t('app.navigation.advancedSearch'),
              permission: 'ADVANCED_SEARCH',
              route: '/search',
              icon: 'search'
            },
            { divider: true },
            {
              label: this.$t('app.navigation.assignments'),
              permission: 'ASSIGNMENT',
              route: '/assignments/listing',
              icon: 'star'
            },
            {
              label: this.$t('app.navigation.permPlacements'),
              permission: 'PERM_PLACEMENT',
              route: '/perm-placements/listing',
              icon: 'anchor'
            },
            {
              label: this.$t('app.navigation.timeSheets'),
              permission: 'TIME_SHEET',
              route: '/time-sheets/listing',
              icon: 'clock'
            },
            {
              label: this.$t('app.navigation.expenses'),
              permission: 'EXPENSE_REPORT',
              route: '/expenses/listing',
              icon: 'dollar-sign'
            },
            {
              label: this.$t('app.navigation.payrollOverviews'),
              permission: 'PAYROLL_OVERVIEW',
              route: '/payroll-overviews/listing',
              icon: 'folder-open'
            },
            {
              label: this.$t('app.navigation.projects'),
              permission: 'PROJECT',
              route: '/projects/list',
              icon: 'diagram-project'
            },
            { divider: true },
            {
              label: this.$t('app.navigation.depositSlips'),
              permission: 'DEPOSIT_SLIP',
              route: '/deposit-slips/listing',
              icon: 'file-invoice-dollar'
            },
            {
              label: this.$t('app.navigation.contractors'),
              permission: 'CONTRACTOR',
              route: '/contractors/listing',
              icon: 'user'
            },
            {
              label: this.$t('app.navigation.clients'),
              permission: 'CLIENT',
              route: '/clients/listing',
              icon: 'briefcase'
            },
            {
              label: this.$t('app.navigation.clientContacts'),
              permission: 'CLIENT_CONTACT',
              route: '/client-contacts/listing',
              icon: 'user-tie'
            },
            {
              label: this.$t('app.navigation.purchaseOrders'),
              permission: 'PURCHASE_ORDER',
              route: '/purchase-orders/listing',
              icon: 'tag'
            },
            {
              label: this.$t('app.navigation.suppliers'),
              permission: 'SUPPLIER',
              route: '/suppliers/listing',
              icon: 'truck'
            },
            {
              label: this.$t('app.navigation.supplierContacts'),
              permission: 'SUPPLIER_CONTACT',
              route: '/supplier-contacts/listing',
              icon: 'users'
            },
            {
              label: this.$t('app.navigation.tesEmployees'),
              permission: 'TES_EMPLOYEE',
              route: '/tes-employees/listing',
              icon: 'user-friends'
            },
            {
              label: this.$t('app.navigation.users'),
              permission: 'USER',
              route: '/users/listing',
              icon: 'laptop'
            }
          ].filter(checkPermissions)
        },
        {
          label: this.$t('app.navigation.recent'),
          items: [
            ...this.recentItems,
            { divider: true },
            {
              label: this.$t('app.navigation.more'),
              route: '/recent',
              icon: 'ellipsis-h'
            }
          ]
        },
        {
          label: this.$t('app.navigation.help'),
          items: [
            {
              label: this.$t('app.navigation.userGuides'),
              route: '/user-guides',
              icon: 'book'
            },
            {
              label: this.$t('app.navigation.emailSupport'),
              href: 'mailto:support@timeonline.com',
              icon: 'envelope'
            }
          ]
        },
        {
          label: this.currentUser.firstName + ' ' + this.currentUser.lastName,
          items: [
            {
              label: this.$t('app.navigation.profile'),
              route: '/profile',
              icon: 'info-circle'
            },
            {
              label: this.$t('app.navigation.preferences'),
              route: '/preferences',
              icon: 'cogs'
            },
            { divider: true },
            {
              label: this.$t('app.navigation.logout'),
              action: this.logout,
              icon: 'lock'
            }
          ]
        }
      ];

      //external user menu is displayed slightly  differently from TES Employees
      let externalUserMenu = [];
      const isTesEmployee =
        this.currentUser && this.currentUser.roles.map(r => r.role_name).includes('TES Employee');
      if (!isTesEmployee) {
        externalUserMenu = menus.filter(m => m.label !== this.$t('app.navigation.menu'));
        externalUserMenu = externalUserMenu.map(m => {
          return m.label !== this.$t('app.navigation.records')
            ? m
            : {
                label: this.$t('app.navigation.menu'),
                items: [
                  {
                    label: this.$t('app.navigation.home'),
                    permission: null,
                    route: '/',
                    icon: 'home'
                  },
                  ...m.items
                ]
              };
        });
        return externalUserMenu.filter(menu => menu.items.filter(item => !item.divider).length);
      }
      // Due to permissions, some menus might be empty, or only contain dividers
      return menus.filter(menu => menu.items.filter(item => !item.divider).length);
    },
    recentItems() {
      const items = this.$store.getters[RecentItemsGetters.RecentItemsForMenu];
      return items.map(item => {
        return {
          label: item.description ? `${item.description.label} (${item.description.name})` : '',
          route: item.description
            ? `/${item.description.urlPart}/${item.objectReferenceNumber}`
            : '',
          icon: item.description ? item.description.icon : ''
        };
      });
    },
    currentLang() {
      return this.$i18n.locale;
    },
    lang() {
      const lang = this.currentLang === 'en' ? 'english' : 'french';
      return this.$t(`app.navigation.${lang}`);
    }
  },
  watch: {
    $route(to, from) {
      if (from.name && from.name.includes('details') && this.$store.state.currentUser) {
        this.$store.dispatch(
          RecentItemsActions.RetrieveRecentItemsForMenu,
          this.$store.state.currentUser.id
        );
      }
    }
  },
  methods: {
    toggleLang(currentLang) {
      const locale = currentLang === 'en' ? 'fr' : 'en';
      this.$store.dispatch(AppActions.SetUserLocale, locale);
      this.$i18n.locale = locale;
    },
    spinnerData(event) {
      this.showSpinner = event;
    },
    async logout() {
      window.onload = null;
      document.onmousemove = null;
      document.onkeydown = null;
      await this.$store.dispatch(AppActions.DismissSession);
      this.$router.replace('/login');
    },
    toggleSmallScreenMenu() {
      this.isSmallScreenMenuVisible = !this.isSmallScreenMenuVisible;
    },
    fadeSearchBar() {},
    handleOpenItem(evt) {
      this.currentOpenMenuId = evt;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/colors.scss';
@import '../styles/variables.scss';

.global-search-spinner {
  display: block;
}

.navigation {
  color: $nav-medium;
  width: $main-content-width;
  margin: 15px auto;
  display: flex;
  flex-direction: column;
  font-size: 120%;
  border-bottom: 1px double;

  .menu {
    background: $nav-dark;
    display: flex;
    .small-screen-menu-icon {
      display: none;
    }
  }
  #right-menu {
    display: flex;
    justify-content: right;
    align-items: center;
    flex-grow: 1;
    justify-content: flex-end;
  }
  .quick-search {
    padding: 20px 0;
    display: flex;
    justify-content: center;

    &.fade {
      display: none;
    }

    .search-container {
      flex-basis: 80%;
    }

    ul li {
      a {
        padding: 0;
        &:hover {
          background-color: inherit;
        }
      }
    }

    .spinner {
      min-width: 50px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  ul {
    display: flex;
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      a {
        text-decoration: none;
        color: inherit;
        padding: 10px 1em;
        display: inline-block;
        width: 100%;
        height: 100%;

        &:hover {
          color: $nav-dark;
          background: $nav-medium;
        }

        > svg {
          margin-top: 5px;
        }
      }
    }

    .lang {
      width: 80px;

      a {
        display: flex;
        justify-content: space-between;
        vertical-align: middle;
        > svg {
          margin-top: 2px;
        }
      }
    }

    .notification {
      a {
        display: flex;
        > svg {
          margin-top: 0px;
        }
      }
      div {
        width: 20px;
        height: 20px;
        line-height: 20px;
        background-color: $red;
        color: $white;
        font-weight: bold;
        font-size: 70%;
        border-radius: 50%;
        text-align: center;
        margin-left: -10px;
        margin-top: -5px;
      }
    }

    .search {
      margin-top: 10px;
      margin-left: 10px;

      input {
        margin-left: 10px;
        background: $nav-medium;
        border: none;
        padding: 5px;
      }
    }
  }

  .small-screen-menu {
    transition-timing-function: ease-in;
    display: none;
    transition: 0.4s;
    height: 0;
    opacity: 0;
    transform: translateY(130%);

    &.menu-is-up {
      transition-timing-function: ease-out;
      transition: 0.4s;
      transform: translateY(0);
      height: 220px;
      opacity: 1;
    }
  }
}

@media print {
  .navigation {
    display: none;
  }
}

@media only screen and (max-width: 768px) {
  .menu #left-menu,
  .menu #right-menu {
    display: none !important;
  }

  .menu .small-screen-menu-icon {
    display: flex !important;
    margin-left: auto;
  }

  .small-screen-menu {
    display: flex !important;
  }

  .small-screen-menu > ul {
    display: block;
    list-style: none;
    padding: 0;
    margin: 0;
    margin-top: 20px;
    width: 100%;
    border-top: 1px solid $row-border;
  }
}
</style>
